import { Link } from 'react-router';
import { Badge, EmptyState } from '../../components/ui';
import { formatDate } from '../listView/TaskTable';
import {
  activeCategoryKey,
  buildProjectColumns,
  categoryTabs,
  filterByCategory,
} from './projectBoard';
import { PROJECT_WORKFLOW_LABELS } from './types';
import type { ProjectListItem } from './types';

/**
 * Projects as a board.
 *
 * Columns are the four project statuses and nothing else. Categories are the
 * strip of pills above them, filtering which cards are on the board without
 * changing what a column means — the same arrangement the Personal Tasks board
 * arrived at, for the same reason: a board that answers "which category" by
 * giving up "what stage" answers the wrong question half the time.
 *
 * Read-only on purpose. Dragging a card between columns would change a
 * project's status, which is a heavier claim than moving a task — it is the
 * kind of thing somebody announces in a standup. The list view's drag already
 * covers moving a project between categories, and status is edited from the
 * project's own page where the person doing it can see what they are changing.
 */
export function ProjectBoardView({
  projects,
  category,
  onCategoryChange,
}: {
  /** Every project the viewer can see, before the category filter. */
  projects: ProjectListItem[];
  /** undefined is All; '' is the real "no category" choice. */
  category: string | undefined;
  onCategoryChange: (next: string | undefined) => void;
}) {
  // Tabs count the WHOLE list; columns are built from the filtered one.
  const tabs = categoryTabs(projects);
  const active = activeCategoryKey(category);
  const columns = buildProjectColumns(filterByCategory(projects, category));
  const shown = columns.reduce((total, column) => total + column.projects.length, 0);

  return (
    <div>
      <div className="section-tabs" role="group" aria-label="Filter board by category">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className="section-tab"
            aria-pressed={tab.key === active}
            // Spelled out: the name and the count are separate elements, and
            // the computed name would otherwise run them together.
            aria-label={`${tab.name} — ${String(tab.count)} ${
              tab.count === 1 ? 'project' : 'projects'
            }`}
            onClick={() => onCategoryChange(tab.category)}
          >
            <span className="section-tab-name">{tab.name}</span>
            <span className="section-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {shown === 0 ? (
        <EmptyState
          title="Nothing in this category"
          description="Choose another category, or All to see every project."
        />
      ) : (
        <div className="board-view">
          {columns.map((column) => (
            <section key={column.key} className="board-column" aria-label={`${column.name} column`}>
              <header className="board-column-header">
                <Badge tone={column.tone}>{column.name}</Badge>
                <span className="board-column-count">
                  {column.projects.length} {column.projects.length === 1 ? 'project' : 'projects'}
                </span>
              </header>
              {column.projects.length === 0 ? (
                <p className="board-column-empty">No projects</p>
              ) : (
                <ul className="board-column-cards">
                  {column.projects.map((project) => (
                    <li key={project.id} className="board-card">
                      <Link className="board-card-button" to={`/projects/${project.id}`}>
                        <span className="board-card-title">{project.name}</span>
                        <span className="board-card-meta">
                          {project.category.trim() === '' ? null : (
                            <span className="board-card-assignee">{project.category}</span>
                          )}
                          {project.due_date === null ? null : (
                            <span
                              className={
                                project.due_date < new Date().toISOString().slice(0, 10) &&
                                project.status !== 'completed'
                                  ? 'board-card-due board-card-due--overdue'
                                  : 'board-card-due'
                              }
                            >
                              Due {formatDate(project.due_date)}
                            </span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/** Re-exported so the page can label the toggle without another import. */
export { PROJECT_WORKFLOW_LABELS };
